import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { ConstantsService } from "@services/constantsService";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as Polyglot from 'node-polyglot';

export enum PluralizationKey {
    One = "one",
    Zero = "zero",
    Other = "other",
}

export interface IPhraseLocale {
    id: string;
    name: string;
    code: string;

    default: boolean;
    rtl: boolean;
    pluralForms: string[];

    createdAt: string;
    updatedAt: string;

    statistics?: IPhraseLocaleStatistics;
}

export interface IPhraseLocaleStatistics {
    keys_total_count: number;
    keys_untranslated_count: number;
    words_total_count: number;
    translations_completed_count: number;
    translations_unverified_count: number;
    unverified_words_count: number;
    missing_words_count: number;

    // --

    translatedCount: number;
    translatedCountProgress: number;
    translationProgressClass: string;

    verificationCount: number;
    verificationCountProgress: number;
    verificationProgressClass: string;
}

export interface ITranslationLocale {
    Locale: string;
    Title: string;
}

export class TranslationService {

    polyglot: Polyglot;
    userOptions: ICombinedPreferences;

    userLanguageCode: string = 'en';

    languageJsonFile: any;

    translationValues: any[] = [];
    languagePluralisation: string[][] = [];

    isVisualizerEnabled: boolean;
    readonly visualizerStorageKey: string = "translationsVisualizerEnabled";

    static $inject = ["$http", "constantsService"]

    constructor(
        private readonly $http: angular.IHttpService,
        private readonly constantsService: ConstantsService) {
        this.isVisualizerEnabled = localStorage.getItem(this.visualizerStorageKey) === "enabled";
    }

    enableVisualizer(): void {
        localStorage.setItem(this.visualizerStorageKey, "enabled");
        this.isVisualizerEnabled = true;
    }

    disableVisualizer(): void {
        localStorage.setItem(this.visualizerStorageKey, "disabled");
        this.isVisualizerEnabled = false;
    }

    initialise(): angular.IPromise<void> {
        this.getLanguageCode();

        return this.getListOfLocales().then((translationLocales: ITranslationLocale[]) => {

            if (!translationLocales?.length) {
                alert("There was a problem obtaining necessary language files. Click Ok to refresh the page and try again.")
                window.location.reload(true);
                return;
            }

            let matchingLocale = translationLocales.find(translationLocale => translationLocale.Locale === this.userLanguageCode);

            if (!matchingLocale) {
                matchingLocale = translationLocales.find(translationLocale => translationLocale.Locale === "en") || translationLocales[0];
            }

            return this.getUserLanguageJsonFile(matchingLocale).then(json => {
                this.languageJsonFile = json;

                this.initialiseLanguagePluralistion();
                this.setPolyglot();
            });
        });
    }

    t(phrase: string, params?: number | string | Polyglot.InterpolationOptions): string {
        return this.translate(phrase, params);
    }

    translate(phrase: string, params?: number | string | Polyglot.InterpolationOptions): string {
        if (!this.languageJsonFile) {
            console.error("Translation Error 1: No translation file available");
            return "T Error 1";
        }

        if (!phrase) {
            console.error(`Translation Error 2: No phrase provided`);
            return "T Error 2";
        }

        if (this.phraseHasUnknownValues(phrase)) {
            console.error(`Translation Error 3: Unknown phrase parameter value for ${phrase}`);
            return "T Error 3";
        }

        let translatedString = "";

        if (params != null) {
            if (typeof params == "number") {
                const param = Number(params);
                const pluralizationKey = this.getPluralizationKey(param);
                const pluralizedPhrase = this.getPluralizedPhrase(phrase, pluralizationKey);

                phrase = (pluralizedPhrase) ? pluralizedPhrase : phrase;

                translatedString = this.polyglot.t(phrase, { 0: param });

            } else if (Array.isArray(params)) {
                // If we are an array, we pass it in, arrays act like parameters to the translation
                // E.g. if we translate "Hello {0} welcome to {1}" then the params might be ["Chris","Verilocation"]
                translatedString = this.polyglot.t(phrase, params);

            } else {
                translatedString = this.polyglot.t(phrase, { 0: params });
            }
        }

        if (!translatedString) {
            translatedString = this.polyglot.t(phrase);
        }

        if (this.userLanguageCode == "qa") {
            return `#${translatedString}#`;
        }

        if (this.isVisualizerEnabled) {
            return `«${translatedString}»`;
        }

        return translatedString;
    }

    tEnum<T>(type: T, enumName: string, value: number, nullCaseFallback = "Unknown"): string {
        return this.translateEnum(type, enumName, value, nullCaseFallback);
    }

    tEnumTitle(enumName: string) {
        return this.t(`${enumName}Enum.Title`);
    }

    translateEnum<T>(type: T, enumName: string, value: number, nullCaseFallback = "Unknown"): string {

        if (typeof value === "undefined" || value == null || !type[value]) {
            return nullCaseFallback;
        }

        var enumKey = enumName.endsWith("Enum")
            ? `${enumName}.${type[value]}`
            : `${enumName}Enum.${type[value]}`;

        if (this.polyglot.has(enumKey)) {
            return this.t(enumKey);
        }

        return type[value];
    }

    getUserLanguageJsonFile(locale: ITranslationLocale): angular.IPromise<any> {

        if (!locale) {
            console.error("getUserLanguageJsonFile has no locale");
            return null;
        }

        return this
            .$http<string>({
                method: "GET",
                url: `/assets/languages/${locale.Locale}.json?t=${new Date().getTime()}`
            })
            .then(response => {
                return response.data;
            }).catch((error) => {
                if (error?.status === 404) {
                    localStorage.setItem('userSelectedLanguageCode', "en");
                }
            });
    }

    getListOfLocales(): angular.IPromise<ITranslationLocale[]> {

        return this
            .$http<ITranslationLocale[]>({
                method: "GET",
                url: `/assets/languages/locales.json`,
                cache: true
            })
            .then((response: any) => {

                let translationLocales: ITranslationLocale[] = response?.data?.locales;

                if (!translationLocales?.length) {
                    console.warn("No language locales available");
                    return;
                }

                translationLocales.sort((l1, l2) => l1.Title.localeCompare(l2.Title));

                if (this.constantsService.getIsProduction()) {
                    return translationLocales.filter(translationLocale => translationLocale.Locale != "qa");
                }

                return translationLocales;

            }).catch((error: any) => {
                console.warn("Failed to get language locales list");
                return null;
            });
    }

    getListOfLocalesRemote(): angular.IPromise<IPhraseLocale[]> {
        return this
            .$http<IPhraseLocale[]>({
                method: "GET",
                url: `/api/translations/locales/remote`
            })
            .then(locales => {
                return locales.data;
            });
    }

    getLocaleDetails(locale: IPhraseLocale): angular.IPromise<IPhraseLocale> {
        return this
            .$http<IPhraseLocale>({
                method: "GET",
                url: `/api/translations/${locale.id}/details`
            })
            .then(response => {
                return response.data;
            });
    }

    //-----------------------

    private getPluralizationKey(params: number): PluralizationKey {
        switch (params) {
            case 0:
                return PluralizationKey.Zero;

            case 1:
                return PluralizationKey.One;

            default:
                return PluralizationKey.Other;
        }
    }

    private getPluralizedPhrase(phrase: string, pluralizationKey: PluralizationKey): string {
        const pluralizedPhrase = `${phrase}.${pluralizationKey}`;

        return (this.polyglot.has(pluralizedPhrase)) ? pluralizedPhrase : null;
    }

    private phraseHasUnknownValues(phrase: string): boolean {
        const phraseEndsWithMissingKeyValue = phrase.indexOf(".") === phrase.length - 1;
        const phraseHasUndefinedKeyValue = phrase.indexOf(".undefined") !== -1;

        return phraseEndsWithMissingKeyValue || phraseHasUndefinedKeyValue;
    }

    private getLanguageCode(): void {
        const userSetLanguage = localStorage.getItem('userSelectedLanguageCode');

        this.userLanguageCode = (userSetLanguage) ? userSetLanguage : "en";
    }

    private setPolyglot() {
        const locale = this.userLanguageCode;
        const phrases = this.languageJsonFile;
        const fixedPhrases = this.fixParameterProblem(phrases);

        this.polyglot = new Polyglot({ locale, phrases: fixedPhrases });
    }

    private initialiseLanguagePluralistion() {
        //zero, one, few, many, other - phrase plurals
        this.languagePluralisation['fr'] = [];
        this.languagePluralisation['fr'][0] = "zero";
        this.languagePluralisation['fr'][1] = "zero";

        this.languagePluralisation['en'] = [];
        this.languagePluralisation['en'][0] = "zero";
        this.languagePluralisation['en'][1] = "one";

        this.languagePluralisation['sp'] = copy(this.languagePluralisation['en']);
        this.languagePluralisation['no'] = copy(this.languagePluralisation['en']);
        this.languagePluralisation['fi'] = copy(this.languagePluralisation['en']);
        this.languagePluralisation['nl'] = copy(this.languagePluralisation['en']);

        // polish set to en plurals just for testing
        this.languagePluralisation['pl'] = copy(this.languagePluralisation['en']);

        //test set up for testing purposes
        this.languagePluralisation['qa'] = copy(this.languagePluralisation['en']);

        //this.languagePluralisation['pl'] = [];
        this.languagePluralisation['se'] = [];
        this.languagePluralisation['dk'] = [];
    }

    private createPhrasePlurals(phrase: string, params: number): string {
        try {
            var pluralForm = this.languagePluralisation[this.userLanguageCode][params] || "other";

            return `${phrase}.${pluralForm}`;
        }
        catch (e) {
            console.log("createPhrasePlurals failed", phrase, params);
            console.error("createPhrasePlurals failed", e);
        }
    }

    private getDefaultLocale(): angular.IPromise<ITranslationLocale> {
        return this.getListOfLocales().then(locales => {
            return locales.find(l => l.Locale == "en");
        });
    }

    private getMainLanguageJson(): angular.IPromise<string> {
        return this
            .$http<string>({
                url: `/assets/languages/en.json`,
                method: "GET",
                cache: false
            })
            .then(response => {
                return response.data;
            });
    }

    private fixParameterProblem(phrases: Object): Object {
        let phrasesString = JSON.stringify(phrases);

        const firstFixRegEx = /([a-zA-Z0-9.,:-])\%\s\{/g;
        const secondFixRegEx = /%\s\{/g;

        phrasesString = phrasesString.replace(firstFixRegEx, "$1 %{");
        phrasesString = phrasesString.replace(secondFixRegEx, "%{");

        return JSON.parse(phrasesString);
    }
}
